import posthog from 'posthog-js';
import { startConnectivitySummary } from './connectivity';
import {
  clearAnonymousQueuedEvents,
  flushAnalyticsQueue,
  startAnalyticsQueueFlushing,
} from './queue';
import { scrubCaptureResult } from './scrubUrlProperties';
import { stampModuleOnSdkEvents } from './sdkEventModule';
import { getAppRelease } from '../appRelease';

/**
 * PostHog bootstrap. Originally identity-only (BIL-4823); now also enables
 * the maintenance_mode feature flag (see maintenanceFlag.ts and
 * MaybeMaintenanceMode.tsx) and session replay (BIL-4826) — see
 * docs/product-analytics-contract.md and
 * plans/product-analytics-2026-08/008-posthog-bootstrap-identity.md and
 * 011-replay-flags-and-split-removal.md.
 *
 * initPostHog() is called at module scope in both entry files
 * (manager.jsx and maquinista.jsx). If a third entry point is ever added,
 * it needs the same call — there is no shared bootstrap module today.
 */

let initialised = false;

export const isPostHogInitialised = (): boolean => initialised;

/**
 * Sign-out: send what the signed-out user still has queued under their own
 * identity, reset PostHog's identity, then drop the anonymous-origin entries
 * that would otherwise be attributed to whoever signs in next on this
 * device (queue.ts, clearAnonymousQueuedEvents). Never throws; a no-op when
 * PostHog was never initialised.
 */
export const resetPostHogIdentity = async (): Promise<void> => {
  if (!initialised) return;
  try {
    await flushAnalyticsQueue();
  } catch (error) {
    console.error('Failed to flush analytics queue before sign-out', error);
  }
  try {
    posthog.reset();
  } catch (error) {
    console.error('Failed to reset PostHog identity', error);
  }
  try {
    await clearAnonymousQueuedEvents();
  } catch (error) {
    console.error('Failed to drop anonymous analytics events', error);
  }
};

// Session replay sample rate (BIL-4826). Maintainer decision (2026-09-14):
// 0.15 by default — ~4.5k of ~30k sessions/month, inside PostHog's 5k/month
// free-tier replay allowance, with the project's replay billing limit kept
// at $0 as a hard ceiling (the operator does not raise it). Overridable via
// VITE_APP_POSTHOG_REPLAY_SAMPLE_RATE for a future rollout change without a
// code deploy.
const DEFAULT_REPLAY_SAMPLE_RATE = 0.15;

const resolveReplaySampleRate = (): number => {
  const raw = import.meta.env.VITE_APP_POSTHOG_REPLAY_SAMPLE_RATE;
  if (!raw) return DEFAULT_REPLAY_SAMPLE_RATE;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1)
    return DEFAULT_REPLAY_SAMPLE_RATE;

  return parsed;
};

export const initPostHog = (): void => {
  try {
    const key = import.meta.env.VITE_APP_POSTHOG_KEY;
    // No-op without a key: local dev, CI and any preview/staging build that
    // didn't receive a POSTHOG_KEY_* secret must never send events or fail.
    if (!key) return;

    posthog.init(key, {
      api_host: import.meta.env.VITE_APP_POSTHOG_HOST,
      // api_host is the first-party reverse proxy (import-env.mjs); the SDK
      // still needs the real PostHog UI for toolbar/links, or it would try
      // to open them on the proxy domain.
      ui_host: 'https://us.posthog.com',
      autocapture: false,
      capture_pageview: false,
      // Off by default whenever capture_pageview is off (the SDK ties the
      // two together), which is why it was missing until BIL-5257. Sent via
      // sendBeacon on unload / tab-hide, through before_send like every
      // other event; it is what gives Web Analytics session duration and
      // bounce rate.
      capture_pageleave: true,
      // Session replay (BIL-4826). Mask every text input by default — the
      // opposite of the removed Smartlook config, which recorded
      // forms/numbers/emails/ips unmasked. Do not set maskAllInputs: false
      // or any "unmask all inputs" convenience here: no email or number
      // input may ever be recorded unmasked. See
      // docs/product-analytics-contract.md.
      session_recording: {
        maskAllInputs: true,
        sampleRate: resolveReplaySampleRate(),
      },
      // BIL-4824 follow-up: posthog-js stamps URL/pathname/referrer
      // properties on every event itself (e.g. $pathname, $session_entry_*,
      // $set_once.$initial_*) outside our control, and does so at flush
      // time (plan 010's queue), so the $current_url override on our own
      // $pageview capture (SentryRoute.tsx) isn't enough. This hook scrubs
      // every URL-shaped property globally — see scrubUrlProperties.ts.
      // Uses `before_send`, not the deprecated `sanitize_properties`: the
      // latter logs a console.error on every single captured event, which
      // Sentry's consoleLoggingIntegration would ship as a log line per
      // event in prod/staging.
      // Two steps, in order: stamp the taxonomy `module` on the SDK's own
      // screen event ($pageleave — sdkEventModule.ts), then the URL scrub.
      before_send: [stampModuleOnSdkEvents, scrubCaptureResult],
    });
    initialised = true;

    // Both apps share one PostHog project (the company group join and the
    // single maintenance_mode toggle require it), so every event must carry
    // which app sent it. The person-property copy of `app` set alongside
    // identity is not enough: a user who touches both apps overwrites it
    // last-write, making person-level `app` unreliable for event filtering.
    posthog.register({
      app: import.meta.env.VITE_APP_ENTRY,
      app_version: getAppRelease(),
    });

    // Only registered on the success path, so nothing listens for network
    // transitions (and no timers/listeners are attached) when PostHog was
    // never initialised — see connectivity.ts.
    startConnectivitySummary();

    // Same reasoning: the queue's boot drain / periodic flush / page-hide
    // flush only start once PostHog is actually initialised — see queue.ts.
    startAnalyticsQueueFlushing();
  } catch (error) {
    // A PostHog failure must never break app boot.
    console.error('Failed to initialise PostHog', error);
  }
};
